<template>
  <AppContainer>
    <div>
      <EPageTitleMobile
        :title="t(useTranslationPath(callerPath, 'activeSessions'))"
        :description="t(useTranslationPath(callerPath, 'activeSessionsDescription'))"
      />
      <EList>
        <EListItem
          v-for="session in activeSessions"
          :id="session.work_order_id"
          :key="`session${session.id}`"
          :title="session.workOrder.summary"
          :description="session.workOrder.primary_location_path"
          :status="session.workOrder.state_name"
          :status-variant="session.workOrder.statusVariant"
          @click="resumeSession(session)"
        >
          <EListItemProperty
            :label="t(useTranslationPath(callerPath, 'startTime'))"
            :value="new Date(session.start_time).toLocaleString()"
          />
          <EListItemProperty
            v-if="session.workOrder?.due_date"
            :label="t(useTranslationPath(callerPath, 'dueBy'))"
            :value="useDayJs(session.workOrder?.due_date).format('L')"
          />
        </EListItem>
      </EList>
    </div>
  </AppContainer>
</template>

<script setup>
import AppContainer from '@/components/AppContainer.vue'
import { useRedirectToError } from '@/composables/useRedirectToError.js'
import { useTranslationPath } from '@/composables/useTranslationPath.js'
import { isAccessCodeEnabled, isLocationEnabled } from '@/composables/useVerificationMethod.js'
import { useAppStore } from '@/stores/AppStore.js'
import { useAuthStore } from '@/stores/authenticated/AuthStore.js'
import { useEngineerStore } from '@/stores/authenticated/EngineerStore.js'
import { useStartWorkCheckStore } from '@/stores/authenticated/StartWorkCheckStore.js'
import { useWorkOrderStore } from '@/stores/authenticated/WorkOrderStore.js'
import { useDayJs } from '@expansivefm/api-request-parser'
import { EList, EListItem, EListItemProperty, EPageTitleMobile } from '@expansivefm/eds'
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const { t } = useI18n()

const router = useRouter()

const appStore = useAppStore()
const authStore = useAuthStore()
const engineerStore = useEngineerStore()
const workOrderStore = useWorkOrderStore()
const startWorkCheckStore = useStartWorkCheckStore()

const callerPath = 'views.authenticated.sessions'

const activeSessions = ref([])

onMounted(async () => {
  appStore.$patch({ showBackButton: false })

  const { data: startChecks } = await startWorkCheckStore.fetchStartWorkChecks()

  await engineerStore
    .sessions(['activeEngineers', 'engineers', 'workOrderStartWorkCheck'], { status: 'OPEN' })
    .then(async ({ data: sessionData }) => {
      if (sessionData.length === 0) {
        await router.push({ name: 'work-order.index' })
      } else {
        await engineerStore.$patch({ previousActiveSessions: sessionData })

        for (const session of sessionData) {
          await workOrderStore.fetchWorkOrder(session.work_order_id).then(({ data }) => {
            let startWorkChecks = null

            if (
              data.workflow_start_work_checks_required &&
              session.workOrderStartWorkCheck &&
              session.workOrderStartWorkCheck.status === 'PASSED'
            ) {
              startWorkChecks = startChecks.find(
                (check) => check.id === session.work_order_start_work_check_id,
              )
            }

            activeSessions.value.push({
              ...session,
              workOrder: {
                ...data,
                // TODO: Add a function to determine the status variant based on work order state
                statusVariant: data.state_status === 'completed' ? 'success' : 'warning',
                startWorkChecks,
              },
            })
          })
        }
      }
    })
})

const resumeSession = async (session) => {
  const currentEngineer =
    session.activeEngineers?.find((engineer) => engineer.id === authStore.user.id) ??
    session.engineers?.find((engineer) => engineer.id === authStore.user.id)

  if (!currentEngineer) {
    await useRedirectToError(router)
      .setTitle(t(useTranslationPath(callerPath, 'engineerNotFound')))
      .setMessage(t(useTranslationPath(callerPath, 'engineerNotFoundDescription')))
      .setStatus(404)
      .redirect()

    return
  }

  if (session.workOrder.startWorkChecks) {
    startWorkCheckStore.$patch({ check: session.workOrder.startWorkChecks })
  }

  let sessionData = {
    location_verification_method_check_in: null,
    activeEngineers: [...(session.activeEngineers ?? []), currentEngineer].reduce(
      (unique, item) => {
        return unique.includes(item) ? unique : [...unique, item]
      },
      [],
    ),
    workOrderStartWorkCheck: session.workOrder.startWorkChecks
      ? session.workOrder.startWorkChecks.id
      : null,
  }

  if (
    (isLocationEnabled('ENGINEER_APP_CHECK_IN_METHOD') ||
      isAccessCodeEnabled('ENGINEER_APP_CHECK_IN_METHOD')) &&
    engineerStore.verificationData.checkIn.method !== null
  ) {
    sessionData = {
      ...sessionData,
      location_verification_method_check_in: engineerStore.verificationData.checkIn.method,
    }

    if (engineerStore.verificationData.checkIn.method === 'GEOLOCATION') {
      sessionData = {
        ...sessionData,
        geolocation_check_in: engineerStore.verificationData.checkIn.data,
      }
    }
  }

  await engineerStore
    .updateSession(session.id, sessionData)
    .then(async () => {
      engineerStore.$patch({ session })

      workOrderStore.$patch({ workOrder: session.workOrder })

      if (
        session.workOrder.startWorkChecks === null &&
        session.workOrder.workflow_start_work_checks_required
      ) {
        await router.push({
          name: 'work-order.start-work-checks.index',
          params: { workOrderId: session.work_order_id },
        })
      } else {
        await router.push({
          name: 'work-order.detail',
          params: { workOrderId: session.work_order_id },
        })
      }
    })
    .catch(async () => {
      await useRedirectToError(router)
        .setTitle(t(useTranslationPath(callerPath, 'unexpectedErrorTitle')))
        .setMessage(t(useTranslationPath(callerPath, 'unexpectedErrorMessage')))
        .setStatus(400)
        .redirect()
    })
}
</script>
